import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { generateClient } from "aws-amplify/api";

import { upsertQuestionnaire } from "@/graphql/mutations";

const client = generateClient();
const QuestionnaireContext = createContext();

export function QuestionnaireProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [meta, setMeta] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("qb_meta");
      return saved ? JSON.parse(saved) : { name: "", purpose: "", id: null };
    }
    return { name: "", purpose: "", id: null };
  });

  const [questions, setQuestionsState] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("qb_questions");
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  const [settings, setSettings] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("qb_settings");
      return saved ? JSON.parse(saved) : { endPages: [] };
    }
    return { endPages: [] };
  });

  useEffect(() => {
    localStorage.setItem("qb_meta", JSON.stringify(meta));
    localStorage.setItem("qb_questions", JSON.stringify(questions));
    localStorage.setItem("qb_settings", JSON.stringify(settings));
  }, [meta, questions, settings]);

  const mkId = () => Math.random().toString(36).slice(2, 9);

  const updateMeta = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  const setQuestions = (newQuestions) => {
    setQuestionsState(newQuestions);
  };

  const addQuestion = (type) => {
    const base = {
      id: mkId(),
      type,
      label: "",
      required: false,
      options:
        type === "multiple"
          ? [
              { text: "", logic: null },
              { text: "", logic: null },
            ]
          : [],
    };
    setQuestionsState((prev) => [...prev, base]);
  };

  const updateQuestion = (id, updates) => {
    setQuestionsState((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updates } : q)),
    );
  };

  const removeQuestion = (id) => {
    setQuestionsState((prev) => prev.filter((q) => q.id !== id));
  };

  const createEndPage = (name, description = "", type = "ineligible") => {
    const newPage = {
      id: mkId(),
      name,
      description,
      type,
    };
    setSettings((prev) => ({
      ...prev,
      endPages: [...prev.endPages, newPage],
    }));
    return newPage;
  };

  const resetQuestionnaire = useCallback(() => {
    setMeta({ name: "", purpose: "", id: null });
    setQuestionsState([]);
    setSettings({ endPages: [] });
    setError(null);
    setLoading(false);

    localStorage.removeItem("qb_meta");
    localStorage.removeItem("qb_questions");
    localStorage.removeItem("qb_settings");
  }, []);

  const saveQuestionnaire = async (overrideId) => {
    setLoading(true);
    setError(null);

    try {
      const targetId = overrideId !== undefined ? overrideId : meta.id;

      const questionnaireInput = {
        id: targetId ? parseInt(targetId) : undefined,
        name: meta.name,
        description: meta.purpose,
        questions_json: JSON.stringify(questions),
        settings_json: JSON.stringify(settings),
      };

      console.log("SENDING TO BACKEND:", questionnaireInput);

      const result = await client.graphql({
        query: upsertQuestionnaire,
        variables: {
          input: {
            questionnaires: questionnaireInput,
          },
        },
      });

      const savedData = result.data.upsertQuestionnaire.questionnaires[0];

      if (savedData && savedData.id) {
        updateMeta("id", savedData.id);
        console.log("SAVED SUCCESSFULLY, ID:", savedData.id);
        return { success: true, id: savedData.id };
      } else {
        throw new Error("No data returned from backend");
      }
    } catch (err) {
      console.error("API Error:", err);
      const errMsg = err.errors ? err.errors[0].message : err.message;
      setError(errMsg || "Failed to save questionnaire");
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    meta,
    questions,
    settings,
    loading,
    error,
    setSettings,
    updateMeta,
    setQuestions,
    addQuestion,
    updateQuestion,
    removeQuestion,
    createEndPage,
    resetQuestionnaire,
    saveQuestionnaire,
  };

  return (
    <QuestionnaireContext.Provider value={value}>
      {children}
    </QuestionnaireContext.Provider>
  );
}

export const useQuestionnaire = () => {
  return useContext(QuestionnaireContext);
};
